import { useState } from 'react'
import { Link } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import { EmptyState } from '../../components/feedback/EmptyState'
import { useClientesV2 } from '../../hooks/useEmpresas'
import { downloadEtiquetasQR, useEquipamentoList } from '../../hooks/useEquipamentos'
import { TIPOS_EQUIPAMENTO } from './schema'
import styles from '../_shared/CrudPage.module.css'

export default function EquipamentosPage() {
  const [busca, setBusca] = useState('')
  const [pagina, setPagina] = useState(1)
  const [cliente, setCliente] = useState('')
  const [tipo, setTipo] = useState('')
  const [ativo, setAtivo] = useState<'true' | 'false' | 'todos'>('true')
  const [statusOperacional, setStatusOperacional] = useState('')
  const [modo, setModo] = useState<'cartoes' | 'lista'>(() =>
    localStorage.getItem('equipamentos-modo') === 'lista' ? 'lista' : 'cartoes',
  )
  const [gerandoEtiquetas, setGerandoEtiquetas] = useState(false)

  const clientes = useClientesV2({ page_size: 500 })
  const { data, isLoading } = useEquipamentoList({
    search: busca || undefined,
    empresa: cliente || undefined,
    tipo: tipo || undefined,
    ativo: ativo === 'todos' ? 'todos' : ativo === 'true',
    status_operacional: statusOperacional || undefined,
    page: pagina,
  })

  function resetPagina() {
    setPagina(1)
  }

  function alterarModo(novoModo: 'cartoes' | 'lista') {
    setModo(novoModo)
    localStorage.setItem('equipamentos-modo', novoModo)
  }

  async function imprimirEtiquetas(formato: 'a4_3x8' | 'zebra_50x30') {
    if (!cliente) {
      notifications.show({ color: 'orange', message: 'Selecione um cliente antes de gerar etiquetas em lote.' })
      return
    }
    try {
      setGerandoEtiquetas(true)
      await downloadEtiquetasQR({ empresa: cliente, formato })
    } catch {
      notifications.show({ color: 'red', message: 'Não foi possível gerar as etiquetas.' })
    } finally {
      setGerandoEtiquetas(false)
    }
  }

  async function imprimirIndividual(id: string) {
    try {
      await downloadEtiquetasQR({ ids: [id], formato: 'zebra_50x30' })
    } catch {
      notifications.show({ color: 'red', message: 'Não foi possível gerar a etiqueta deste equipamento.' })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Equipamentos</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link
            to="/equipamentos/importar"
            className={styles.btnNovo ?? ''}
            style={{
              background: 'white',
              color: '#1565C0',
              border: '2px solid #1565C0',
              textDecoration: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontWeight: 600,
            }}
          >
            📊 Importar planilha
          </Link>
          <Link to="/equipamentos/novo" className={styles.btnNovo ?? ''}>
            + Novo Equipamento
          </Link>
        </div>
      </header>

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          placeholder="Buscar por marca, modelo ou série..."
          value={busca}
          onChange={(e) => { setBusca(e.target.value); resetPagina() }}
        />
        <select value={cliente} onChange={(e) => { setCliente(e.target.value); resetPagina() }} aria-label="Filtrar por cliente">
          <option value="">Todos os clientes</option>
          {clientes.data?.results.map((item) => <option key={item.id} value={item.id}>{item.razao_social}</option>)}
        </select>
        <select value={tipo} onChange={(e) => { setTipo(e.target.value); resetPagina() }} aria-label="Filtrar por tipo">
          <option value="">Todos os tipos</option>
          {TIPOS_EQUIPAMENTO.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
        <select value={ativo} onChange={(e) => { setAtivo(e.target.value as typeof ativo); resetPagina() }} aria-label="Filtrar por situação">
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
          <option value="todos">Ativos e inativos</option>
        </select>
        <select value={statusOperacional} onChange={(e) => { setStatusOperacional(e.target.value); resetPagina() }} aria-label="Filtrar por estado operacional">
          <option value="">Qualquer estado</option>
          <option value="operando">Operando</option>
          <option value="desligado">Desligado</option>
          <option value="avariado">Avariado / manutenção</option>
          <option value="removido">Removido / desativado</option>
        </select>
        {(busca || cliente || tipo || ativo !== 'true' || statusOperacional) && (
          <button type="button" onClick={() => { setBusca(''); setCliente(''); setTipo(''); setAtivo('true'); setStatusOperacional(''); resetPagina() }}>
            Limpar filtros
          </button>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" onClick={() => alterarModo('cartoes')} aria-pressed={modo === 'cartoes'}>Cartões</button>
          <button type="button" onClick={() => alterarModo('lista')} aria-pressed={modo === 'lista'}>Lista</button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" disabled={gerandoEtiquetas || !cliente} onClick={() => imprimirEtiquetas('a4_3x8')}>Etiquetas A4 (3 × 8)</button>
          <button type="button" disabled={gerandoEtiquetas || !cliente} onClick={() => imprimirEtiquetas('zebra_50x30')}>Etiquetas Zebra 50 × 30 mm</button>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loading}>Carregando equipamentos...</div>
      ) : (
        <>
          {modo === 'cartoes' ? <div className={styles.grid}>
            {data?.results.map((eq) => (
              <Link
                key={eq.id}
                to={`/equipamentos/${eq.id}`}
                className={styles.card}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div className={styles.cardHeader}>
                  <span className={styles.tipo}>{eq.tipo.replace(/_/g, ' ')}</span>
                  <span className={`${styles.badge} ${eq.ativo ? styles.badgeActive : styles.badgeInactive}`}>
                    {eq.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <h3 className={styles.cardTitle}>{eq.marca} {eq.modelo}</h3>
                {eq.numero_serie && <p className={styles.cardSub}>S/N: {eq.numero_serie}</p>}
                {eq.empresa_nome && <p className={styles.cardSub}>{eq.empresa_nome}</p>}
                {eq.area_nome && <p className={styles.cardSub}>Área: {eq.area_nome}</p>}
                {eq.capacidade_btu != null && (
                  <p className={styles.cardSub}>
                    {Number(eq.capacidade_btu).toLocaleString('pt-BR')} BTU
                    {eq.capacidade_tr != null && ` • ${Number(eq.capacidade_tr).toFixed(1)} TR`}
                  </p>
                )}
                <div className={styles.cardFooter}>
                  <span className={styles.qrToken}>QR: {eq.qr_code_token.slice(0, 8)}...</span>
                </div>
              </Link>
            ))}
          </div> : (
            <div style={{ overflowX: 'auto', background: 'white', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['Equipamento', 'Tipo', 'Cliente', 'Local', 'Capacidade', 'Situação', 'QR'].map((h) => <th key={h} style={{ textAlign: 'left', padding: 10, borderBottom: '2px solid #ddd' }}>{h}</th>)}</tr></thead>
                <tbody>{data?.results.map((eq) => (
                  <tr key={eq.id}>
                    <td style={{ padding: 10, borderBottom: '1px solid #eee' }}><Link to={`/equipamentos/${eq.id}`} style={{ fontWeight: 600 }}>{eq.marca} {eq.modelo}</Link>{eq.numero_serie && <div style={{ fontSize: 12, color: '#666' }}>S/N {eq.numero_serie}</div>}</td>
                    <td style={{ padding: 10, borderBottom: '1px solid #eee' }}>{eq.tipo.replace(/_/g, ' ')}</td>
                    <td style={{ padding: 10, borderBottom: '1px solid #eee' }}>{eq.empresa_nome || '—'}</td>
                    <td style={{ padding: 10, borderBottom: '1px solid #eee' }}>{eq.area_nome || '—'}</td>
                    <td style={{ padding: 10, borderBottom: '1px solid #eee' }}>{eq.capacidade_btu != null ? `${Number(eq.capacidade_btu).toLocaleString('pt-BR')} BTU/h` : '—'}</td>
                    <td style={{ padding: 10, borderBottom: '1px solid #eee' }}>{eq.ativo ? 'Ativo' : 'Inativo'}</td>
                    <td style={{ padding: 10, borderBottom: '1px solid #eee' }}><button type="button" onClick={() => imprimirIndividual(eq.id)}>Imprimir</button></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {data && (data.next || data.previous) && (
            <div className={styles.pagination}>
              <button disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)}>← Anterior</button>
              <span>Página {pagina} • {data.count} equipamentos</span>
              <button disabled={!data.next} onClick={() => setPagina((p) => p + 1)}>Próxima →</button>
            </div>
          )}
          {data?.results.length === 0 && (
            <EmptyState
              icon="🔧"
              title={busca ? 'Nenhum equipamento bate com a busca' : 'Nenhum equipamento cadastrado'}
              description={
                busca
                  ? 'Tente outro termo de busca ou limpe o filtro.'
                  : 'Cadastre o primeiro equipamento (split, chiller, UTA…) ou importe vários de uma vez via planilha.'
              }
              actionTo={busca ? undefined : '/equipamentos/novo'}
              actionLabel={busca ? undefined : '+ Novo equipamento'}
              secondaryActionTo={busca ? undefined : '/equipamentos/importar'}
              secondaryActionLabel={busca ? undefined : '📥 Importar planilha'}
            />
          )}
        </>
      )}
    </div>
  )
}
